import React, { useEffect, useState } from "react";

import { useFormik } from "formik";
import * as Yup from "yup";
import { X } from "lucide-react";

import Select from "react-select";
import { ActionMeta, SingleValue } from "react-select";

//store
import { useAppDispatch, useAppSelector } from "slices/hook";
import { AppDispatch } from "slices/store";
import { getCoreDataState } from "slices/core-data/reducer";
import { userRolesGetThunk } from "slices/core-data/thunk";

//models
import { Tenant } from "models/tenant/tenant";
import { LabelValuePair } from "models/label-value";

//data
import { CountryCodes } from "models/settings/master-data";
import { BrandingData } from "admin-portal/settings/branding";

//components
import CountrySelector from "admin-portal/shared/country-selector";

import { User } from "models/user/user";
import { AddUserToTenant } from "api/tenant";
import LoadingComponent from "common/components/loader";
import { TenantType, UserRoles } from "models/enums";
import { UserRole } from "models/user/user-role";

interface Props {
  tenant?: Tenant;
}
const UserInvitationComponent: React.FC<Props> = ({ tenant }) => {
  // /userRolesGetThunk
  const dispatch: AppDispatch = useAppDispatch();
  const { userRoles } = useAppSelector(getCoreDataState);

  const [userRoleOptions, setUserRoleOptions] = useState<{ value: string; label: string }[]>([
    { value: "", label: "Select Role" },
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [user, setUser] = useState<User>();

  useEffect(() => {
    dispatch(userRolesGetThunk());
  }, []);

  useEffect(() => {
    const _isSystem = tenant?.tenantTypeId == TenantType.SYSTEM;
    const _roles = _isSystem
      ? [UserRoles.SYSTEM_ADMIN, UserRoles.SYSTEM_SUPER_ADMIN, UserRoles.SYSTEM_USER]
      : [UserRoles.TENANT_ADMIN, UserRoles.TENANT_MANAGER, UserRoles.TENANT_STAFF];
    const _data = userRoles
      .filter((r) => _roles.findIndex((x) => x == r.dbKey) >= 0)
      .map((role) => ({
        value: role.id ?? "",
        label: role.name ?? "",
        dbKey: role.dbKey ?? "",
      }));
    setUserRoleOptions([{ value: "", label: "Select Role" }, ..._data]);
    /* if (formik.values.role == "" && userRoles.length > 0) {
      setUserRoleOptions([{ value: "", label: "Select Role" }, ..._data]);
         formik.setFieldValue("role", userRoles[0].id);
    } */
  }, [userRoles]);

  const formik: any = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      username: "",
      phoneNo: "",
      role: null,
      email: "",
      isActive: "",
      employeeNo: "",
      countryCode: (CountryCodes?.length ?? 0) > 0 ? CountryCodes[0] : null,
    },
    validationSchema: Yup.object().shape({
      firstName: Yup.string().required("First Name is required"),
      lastName: Yup.string().required("Last Name is required."),
      countryCode: Yup.object().shape({
        label: Yup.string().required("Country is required"),
        value: Yup.string().required("Country code is required"),
      }),
      phoneNo: Yup.string()
        .matches(/^[\d\s]+$/, "Phone number must contain only numbers and spaces")
        .required("Phone Number is required"),
      email: Yup.string().email("Please enter a valid email"),
      username: Yup.string().required("Username is required"),
      role: Yup.object()
        .shape({
          label: Yup.string().required("Role is required"),
          value: Yup.string(),
          dbKey: Yup.string(),
        })
        .required(`A role must be selected`),
    }),
    onSubmit: (values: any) => {
      // console.log(values);
      sendInvitation(values);
    },
  });

  const sendInvitation = async (values: any) => {
    try {
      setError("");
      setSuccess("");
      setLoading(true);
      console.log(formik.values);

      var _user: User = {
        firstName: values.firstName,
        lastName: values.lastName,
        username: values.username,
        phone: values.phoneNo,
        email: values.email,

        activeTenantId: tenant?.id ?? "",
        activeRole: {
          id: values.role.value,
          name: values.role.dbKey,
        },
      };
      const res = await AddUserToTenant(tenant?.id ?? "", _user);

      if (!(res?.status?.isError ?? false) && (res?.data?.id ?? "") != "") {
        setSuccess("success");
        console.log(res?.data);
        setUser(res.data!);
        //setSelectedTenant(res!.data!);
        //onTenantSaveSuccess(res!.data!);
      } else {
        setError(`error: ${res?.status?.errorCode ?? "500"}:${res?.status?.errorMessage ?? ""}`);
      }
      setLoading(false);
    } catch (error) {
      setError("Something went wrong...");
      setLoading(false);
    }
  };
  const handleCountryChange = (newValue: SingleValue<LabelValuePair>, actionMeta: ActionMeta<LabelValuePair>) => {
    formik.setFieldValue("countryCode", newValue);
  };

  // Auto-populate username when countryCode or phoneNo changes
  useEffect(() => {
    const { countryCode, phoneNo } = formik.values;

    if (countryCode && phoneNo) {
      const countryValue = countryCode.value || "";
      const formattedPhoneNo = phoneNo.replace(/[^0-9]/g, ""); // Remove non-numeric characters

      formik.setFieldValue("username", `${countryValue}${formattedPhoneNo}`);
    }
  }, [formik.values.countryCode, formik.values.phoneNo]);

  return (
    <>
      <form
        action="#!"
        id="userForm"
        onSubmit={(event: any) => {
          event.preventDefault();
          formik.handleSubmit();
        }}
      >
        {!tenant ? (
          <div>Users can be invited after creating a {BrandingData.bussinessDomain}.</div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-x-5">
              <div className="grid grid-cols-1 gap-x-5 xl:grid-cols-2">
                <div className="mb-4">
                  <label htmlFor="firstNameInput" className="inline-block mb-2 text-base font-medium">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                    placeholder="First Name"
                    value={formik.values.firstName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.firstName && formik.errors.firstName ? (
                    <p id="firstName" className="mt-1 text-sm text-red-500">
                      {formik.errors.firstName}
                    </p>
                  ) : null}
                </div>
                <div className="mb-4">
                  <label htmlFor="lastNameInput" className="inline-block mb-2 text-base font-medium">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                    placeholder="Last Name"
                    value={formik.values.lastName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.lastName && formik.errors.lastName ? (
                    <p id="lastName" className="mt-1 text-sm text-red-500">
                      {formik.errors.lastName}
                    </p>
                  ) : null}
                </div>

                <div className="mb-4">
                  <label htmlFor="phoneNumberInput" className="inline-block mb-2 text-base font-medium">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <div className="flex flex-col">
                    <div className="flex flex-row">
                      <div className="w-1/3">
                        <CountrySelector selectedCountry={formik.values.countryCode} onChange={handleCountryChange} />
                      </div>
                      <div className="w-2/3">
                        <input
                          type="text"
                          id="phoneNo"
                          className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                          placeholder="77 456 1789"
                          value={formik.values.phoneNo}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                      </div>
                    </div>
                    <div>
                      {formik.touched.phoneNo && formik.errors.phoneNo ? (
                        <p id="phoneNo" className="mt-1 text-sm text-red-500">
                          {formik.errors.phoneNo}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </div>
                <div className="mb-4">
                  <label htmlFor="emailInput" className="inline-block mb-2 text-base font-medium">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                    placeholder="example@gmail.com"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                  />
                  {formik.touched.email && formik.errors.email ? (
                    <p id="email" className="mt-1 text-sm text-red-500">
                      {formik.errors.email}
                    </p>
                  ) : null}
                </div>
                <div className="mb-4">
                  <label htmlFor="usernameInput" className="inline-block mb-2 text-base font-medium">
                    Username <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="username"
                    name="username"
                    className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                    placeholder="94774561789"
                    value={formik.values.username}
                    onChange={formik.handleChange}
                  />
                  {formik.touched.username && formik.errors.username ? (
                    <p id="username" className="mt-1 text-sm text-red-500">
                      {formik.errors.username}
                    </p>
                  ) : null}
                </div>
                <div className="mb-4">
                  <label htmlFor="fromInput" className="inline-block mb-2 text-base font-medium">
                    User Role
                  </label>
                  <Select
                    className="border-slate-200 focus:outline-none focus:border-custom-500"
                    options={userRoleOptions}
                    isSearchable={false} // To disable search
                    name="role"
                    id="role"
                    value={formik.values.role}
                    onChange={(option) => formik.setFieldValue("role", option)}
                    // onChange={onChange}
                    //value={userRoleOptions.find((option: any) => option.value === formik.values.role)}
                  />
                  {formik.touched.role && formik.errors.role ? (
                    <p id="role" className="mt-1 text-sm text-red-500">
                      {formik.errors.role}
                    </p>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button
                type="button"
                onClick={() => {
                  formik.handleReset();
                  setUser(undefined);
                }}
                className="text-red-500 bg-white btn hover:text-red-500 hover:bg-red-100 focus:text-red-500 focus:bg-red-100 active:text-red-500 active:bg-red-100 dark:bg-zink-700 dark:hover:bg-red-500/10 dark:focus:bg-red-500/10 dark:active:bg-red-500/10"
              >
                <X className="inline-block size-4" /> <span className="align-middle">Reset</span>
              </button>
              <button
                disabled={(user?.id ?? "") != ""}
                type="submit"
                className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
              >
                Send Invitation
              </button>
            </div>
          </>
        )}
      </form>
      {loading && <LoadingComponent />}
      {success && (
        <div
          className="px-4 py-3 mt-3 text-sm text-green-500 border border-green-200 rounded-md bg-green-50 dark:bg-green-400/20 dark:border-green-500/50"
          id="successAlert"
        >
          <b>Success! </b>
          {<>User has been invited</>}
        </div>
      )}
      {error && (
        <div
          className="px-4 py-3 mt-3 text-sm text-red-500 border border-red-200 rounded-md bg-red-50 dark:bg-red-400/20 dark:border-red-500/50"
          id="successAlert"
        >
          <>
            {error}
            <br />
            Oops! Something went wrong while attempting to invite user to the tenant. Please try again later or contact
            support for assistance.
          </>
        </div>
      )}
    </>
  );
};

export default UserInvitationComponent;
